import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { Barra, BotaoExportar, LinhaEscopo, Rolagem, TD, TH, usePeriodo } from './comum';
import type { LinhaQuadro, Quadro as QuadroApi } from './tipos';

const COLUNAS = ['no_periodo', 'ativos', 'afastados', 'admitidos', 'desligados'] as const;

/** E1 · Quadro de pessoas no período por departamento e função (só contagens; exige pessoas.ver no servidor). */
export function Quadro() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const params = { de: periodo.de, ate: periodo.ate };
  const q = useQuery({ queryKey: ['/relatorios/quadro', params], queryFn: () => get<QuadroApi>('/relatorios/quadro', params), placeholderData: keepPreviousData });
  const linha = (l: Omit<LinhaQuadro, 'departamento_id' | 'funcao_id'> & { departamento: string | null; funcao: string | null }, chave: string, total = false) => (
    <tr key={chave} className={`border-b border-borda last:border-0 ${total ? 'bg-sup-2 font-semibold' : ''}`}>
      <th scope="row" className={`${TD} max-w-56 text-left break-words ${total ? '' : 'font-normal'}`}>
        {total ? t('comum.total') : (l.departamento ?? t('quadro.semDepartamento'))}
      </th>
      <td className={`${TD} max-w-56 break-words text-tinta-2`}>{total ? '' : (l.funcao ?? t('quadro.semFuncao'))}</td>
      {COLUNAS.map((k) => (
        <td key={k} className={`${TD} numero text-right`}>
          {l[k]}
        </td>
      ))}
    </tr>
  );
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <BotaoExportar relatorio="quadro" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <LinhaEscopo escopo={d.escopo} />
            {d.linhas.length === 0 ? (
              <Cartao>
                <Vazio icone={<Users className="size-5" aria-hidden />} titulo={t('quadro.vazioTitulo')} texto={t('quadro.vazioTexto')} />
              </Cartao>
            ) : (
              <Cartao semPadding>
                <Rolagem rotulo={t('comum.tabela')}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className={TH}>{t('quadro.departamento')}</th>
                        <th scope="col" className={TH}>{t('quadro.funcao')}</th>
                        {COLUNAS.map((k) => (
                          <th key={k} scope="col" className={`${TH} text-right`}>{t(`quadro.colunas.${k}`)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.linhas.map((l) => linha(l, `${l.departamento_id ?? 'sem'}:${l.funcao_id ?? 'sem'}`))}
                      {linha({ ...d.totais, departamento: null, funcao: null }, 'total', true)}
                    </tbody>
                  </table>
                </Rolagem>
                <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('quadro.ajuda')}</p>
              </Cartao>
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
